import {
  validateMonthMembership,
  type MonthValidationResult
} from "@/features/profit-calculators/calculator-shared/utils/monthValidation"
import type { MeeshoPaymentFile } from "@/features/profit-calculators/calculator-shared/types"
import { getMeeshoMonthLabel } from "@/features/profit-calculators/meesho-calculator/utils/parsePaymentFile"

/** Validates Meesho order dates (CsvOrderRow.orderDate) against the selected month. */
const validateMeeshoMonth = (
  orderDates: string[],
  targetMonthLabel: string,
  thresholdPct?: number
): MonthValidationResult => validateMonthMembership(orderDates, targetMonthLabel, getMeeshoMonthLabel, thresholdPct)

const getPeriodStartMonth = (periodLabel: string): string | null => {
  const match = periodLabel.match(/^\s*\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})\b/i)
  if (!match) return null
  const month = `${match[1][0].toUpperCase()}${match[1].slice(1, 3).toLowerCase()}`
  return `${month} ${match[2]}`
}

/**
 * Validates a payment ZIP against the month required by its upload slot.
 * The date range encoded in the payment report filename is authoritative.
 * Older reports without a dated filename fall back to their transaction dates.
 */
const validateMeeshoPaymentMonth = (
  paymentFile: MeeshoPaymentFile,
  targetMonthLabel: string
): MonthValidationResult => {
  const periodMonth = getPeriodStartMonth(paymentFile.periodLabel)
  if (periodMonth) {
    const valid = periodMonth === targetMonthLabel
    return {
      valid,
      matchedPct: valid ? 100 : 0,
      detectedMonths: [{ label: periodMonth, pct: 100 }],
      reason: valid
        ? undefined
        : `This payment ZIP is for ${periodMonth}. Please upload the payment ZIP for ${targetMonthLabel}.`
    }
  }

  const transactionDates = [
    ...paymentFile.orders.map(order => order.paymentDate),
    ...paymentFile.ads.map(ad => ad.deductionDate),
    ...paymentFile.referrals.map(referral => referral.paymentDate),
    ...paymentFile.compensations.map(compensation => compensation.date)
  ]
  const validation = validateMonthMembership(transactionDates, targetMonthLabel, getMeeshoMonthLabel)

  if (validation.valid) return validation
  if (validation.detectedMonths.length === 0) {
    return {
      ...validation,
      reason: `Could not determine the payment month in "${paymentFile.fileName}". Please upload a valid ${targetMonthLabel} payment ZIP.`
    }
  }

  return {
    ...validation,
    reason: `This payment ZIP is for ${validation.detectedMonths[0].label}. Please upload the payment ZIP for ${targetMonthLabel}.`
  }
}

export { validateMeeshoMonth, validateMeeshoPaymentMonth }
